import { withRetryParam } from 'utils/gatewayRetry'

const REACT_APP_IMAGE_OPT = process.env.REACT_APP_IMAGE_OPT

export type OptimizedImageType =
  | 'item'
  | 'banner'
  | 'detail'
  | 'activity'
  | 'logo-sm'
  | 'hero'
  | 'logo'
  | 'pfp'
  | 'no-resize'

export const getOptimizedImageUrl = (
  imageUrl: string | null | undefined,
  type: OptimizedImageType
): string | undefined => {
  if (!imageUrl) {
    return undefined
  }

  // don't use optimizer for base64 string
  if (imageUrl.slice(0, 5) === 'data:') {
    return imageUrl
  }

  let height: number | undefined
  let width: number | undefined
  let fit: 'cover' | undefined
  switch (type) {
    case 'item':
      height = 384
      width = 384
      break
    case 'logo-sm':
      height = 128
      width = 128
      break
    case 'logo':
      height = 256
      width = 256
      break
    case 'pfp':
      height = 64
      width = 64
      break
    case 'banner':
      height = 288
      width = 1440
      fit = 'cover'
      break
    case 'hero':
      height = 472
      width = 1616
      fit = 'cover'
      break
    case 'detail':
      height = 800
      width = 800
      break
    case 'activity':
      width = 64
      height = 64
      break
    case 'no-resize':
      break
  }

  const [path] = imageUrl.split('?')
  let format: 'avif' | 'webp' | undefined
  if (path.endsWith('avif')) {
    format = 'avif'
  } else if (path.endsWith('webp')) {
    format = 'webp'
  }

  // configure cloudflare CDN
  let cloudflareOptions =
    width && height ? `width=${width},height=${height}` : ''
  if (fit) {
    cloudflareOptions += `,fit=${fit}`
  }
  if (format) {
    cloudflareOptions += `,format=${format}`
  }

  return REACT_APP_IMAGE_OPT
    ? `${REACT_APP_IMAGE_OPT}/${cloudflareOptions}/${imageUrl}`
    : imageUrl
}

/**
 * Image URLs to try in order: the resized URL, the resized URL with a retry
 * query, then the source URL with a retry query. Duplicates are dropped, so
 * only IPFS gateway images get the retry step.
 */
export const getImageSources = (
  imageUrl: string | undefined,
  type: OptimizedImageType
): string[] => {
  if (!imageUrl) {
    return []
  }
  const sources = [
    getOptimizedImageUrl(imageUrl, type),
    getOptimizedImageUrl(withRetryParam(imageUrl, 1), type),
    withRetryParam(imageUrl, 2)
  ].filter((source): source is string => !!source)
  return Array.from(new Set(sources))
}

export const isVideoUrl = (url: string): boolean => {
  return /\.(mp4|webm|mov)$/.test(url)
}

export const isImageUrl = (url: string): boolean =>
  /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i.test(url)
